import { useState, useEffect } from 'react';
import type { Order } from '@/services/orders';
import { Card } from './Card';
import {
  detectWorkflow,
  STEP_LABELS,
  ADVANCE_LABELS,
  STATUS_TRANSITIONS,
  TERMINAL_STATUSES,
  FAILABLE_STATUSES,
} from './constants';

interface StatusStepperProps {
  readonly order: Order;
  readonly onAdvance: (nextStatus: string) => void;
  readonly onMarkFailed: () => void;
  readonly onCancelReject: () => void;
  readonly isAdvancing: boolean;
  readonly advanceError?: string | null;
}

export const StatusStepper = ({
  order,
  onAdvance,
  onMarkFailed,
  onCancelReject,
  isAdvancing,
  advanceError,
}: StatusStepperProps) => {
  const [justAdvanced, setJustAdvanced] = useState(false);
  const [prevStatus, setPrevStatus] = useState(order.status);

  // Show "Done" when status actually changes (mutation success), not on click
  useEffect(() => {
    if (order.status !== prevStatus) {
      setPrevStatus(order.status);
      setJustAdvanced(true);
      const timer = setTimeout(() => setJustAdvanced(false), 1500);
      return () => clearTimeout(timer);
    }
  }, [order.status, prevStatus]);

  const workflow = detectWorkflow(order.events);
  const eventMap = new Map(order.events.map((e) => [e.status, e]));
  const completedSet = new Set(order.events.map((e) => e.status));
  const currentIdx = workflow.indexOf(order.status);
  const isTerminal = TERMINAL_STATUSES.has(order.status);
  const isFailed = order.status === 'failed';
  const isDelivered = order.status === 'delivered';
  const canFail = FAILABLE_STATUSES.has(order.status);
  const nextStatus = STATUS_TRANSITIONS[order.status];
  const advanceLabel = ADVANCE_LABELS[order.status];

  const handleAdvance = () => {
    if (!nextStatus || isAdvancing) return;
    onAdvance(nextStatus);
  };

  return (
    <Card>
      <div
        style={{
          fontSize: '14px',
          fontWeight: 'var(--font-weight-medium)',
          color: 'var(--text-primary)',
          marginBottom: '12px',
        }}
      >
        Status
      </div>

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {workflow.map((status, i) => {
          const isCompleted = completedSet.has(status) && (i < currentIdx || isTerminal || isDelivered);
          const isCurrent = status === order.status && !isTerminal;
          const event = eventMap.get(status);
          const isLast = i === workflow.length - 1;

          return (
            <StepItem
              key={status}
              label={STEP_LABELS[status] ?? status}
              timestamp={event ? formatStepTime(event.timestamp) : undefined}
              isCompleted={isCompleted}
              isCurrent={isCurrent}
              isLast={isLast}
              showLine={!isLast}
              lineCompleted={isCompleted && i < currentIdx}
            />
          );
        })}
      </div>

      {/* Error banner */}
      {advanceError && (
        <div
          style={{
            fontSize: '12px',
            color: 'var(--color-error-600)',
            backgroundColor: 'var(--bg-error-subtle)',
            padding: '6px 10px',
            borderRadius: 'var(--radius-md)',
            marginTop: '10px',
          }}
        >
          {advanceError}
        </div>
      )}

      {/* Advance button */}
      {nextStatus && !isTerminal && (
        <button
          onClick={handleAdvance}
          disabled={isAdvancing}
          style={{
            width: '100%',
            padding: '9px',
            minHeight: '44px',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            backgroundColor: 'var(--text-primary)',
            color: 'var(--bg-surface)',
            fontSize: '12px',
            fontWeight: 'var(--font-weight-medium)',
            cursor: isAdvancing ? 'not-allowed' : 'pointer',
            marginTop: '14px',
            opacity: isAdvancing ? 0.6 : 1,
            transition: 'opacity 150ms',
          }}
        >
          {isAdvancing
            ? 'Updating...'
            : justAdvanced
              ? 'Done'
              : `${advanceLabel} \u2192`}
        </button>
      )}

      {/* Delivered — disabled complete button */}
      {isDelivered && (
        <button
          disabled
          style={{
            width: '100%',
            padding: '9px',
            minHeight: '44px',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            backgroundColor: 'var(--text-primary)',
            color: 'var(--bg-surface)',
            fontSize: '12px',
            fontWeight: 'var(--font-weight-medium)',
            cursor: 'not-allowed',
            marginTop: '14px',
            opacity: 0.4,
          }}
        >
          Order complete
        </button>
      )}

      {/* Re-queue button for failed orders */}
      {isFailed && (
        <button
          onClick={() => onAdvance('queued')}
          disabled={isAdvancing}
          style={{
            width: '100%',
            padding: '9px',
            minHeight: '44px',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            backgroundColor: 'var(--text-primary)',
            color: 'var(--bg-surface)',
            fontSize: '12px',
            fontWeight: 'var(--font-weight-medium)',
            cursor: isAdvancing ? 'not-allowed' : 'pointer',
            marginTop: '14px',
            opacity: isAdvancing ? 0.6 : 1,
          }}
        >
          {isAdvancing ? 'Updating...' : 'Re-queue order'}
        </button>
      )}

      {/* Mark as failed button */}
      {canFail && (
        <button
          onClick={onMarkFailed}
          style={{
            width: '100%',
            padding: '7px',
            minHeight: '36px',
            border: '0.5px solid var(--admin-amber-border)',
            borderRadius: 'var(--radius-md)',
            backgroundColor: 'transparent',
            color: 'var(--admin-amber-text)',
            fontSize: '11px',
            cursor: 'pointer',
            marginTop: '6px',
            transition: 'background-color 150ms',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--admin-amber-bg)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
        >
          Mark as failed
        </button>
      )}

      {/* Cancel/reject link */}
      {!isTerminal && (
        <div
          onClick={onCancelReject}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onCancelReject(); }}
          style={{
            fontSize: '11px',
            color: 'var(--text-tertiary)',
            textAlign: 'center',
            marginTop: '8px',
            padding: '8px 0',
            minHeight: '36px',
            cursor: 'pointer',
            transition: 'color 150ms',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--text-secondary)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-tertiary)'; }}
        >
          Cancel or reject order...
        </div>
      )}
    </Card>
  );
};

// Step item sub-component
const StepItem = ({
  label,
  timestamp,
  isCompleted,
  isCurrent,
  isLast,
  showLine,
  lineCompleted,
}: {
  readonly label: string;
  readonly timestamp?: string;
  readonly isCompleted: boolean;
  readonly isCurrent: boolean;
  readonly isLast: boolean;
  readonly showLine: boolean;
  readonly lineCompleted: boolean;
}) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: '8px',
      position: 'relative',
      paddingBottom: isLast ? 0 : '16px',
    }}
  >
    {/* Connecting line */}
    {showLine && (
      <div
        style={{
          position: 'absolute',
          left: '6.5px',
          top: '16px',
          width: '1.5px',
          height: 'calc(100% - 8px)',
          backgroundColor: lineCompleted
            ? 'var(--text-accent)'
            : 'var(--border-default)',
        }}
      />
    )}

    {/* Dot */}
    <div
      style={{
        width: '14px',
        height: '14px',
        borderRadius: '50%',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        zIndex: 1,
        ...(isCompleted
          ? {
              backgroundColor: 'var(--text-accent)',
              border: '1.5px solid var(--text-accent)',
            }
          : isCurrent
            ? {
                backgroundColor: 'var(--bg-accent-subtle)',
                border: '1.5px solid var(--text-accent)',
              }
            : {
                backgroundColor: 'var(--bg-surface)',
                border: '1.5px solid var(--border-default)',
              }),
      }}
    >
      {isCompleted && (
        <svg width="8" height="8" viewBox="0 0 10 10">
          <path
            d="M2 5l2.5 2.5L8 3"
            fill="none"
            stroke="var(--text-inverse)"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </div>

    {/* Label + time */}
    <div>
      <div
        style={{
          fontSize: '12px',
          lineHeight: '1.3',
          color: isCompleted || isCurrent ? 'var(--text-primary)' : 'var(--text-tertiary)',
          fontWeight: isCurrent ? 'var(--font-weight-medium)' : 'var(--font-weight-regular)',
        }}
      >
        {label}
      </div>
      {timestamp && (
        <div style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>
          {timestamp}
        </div>
      )}
    </div>
  </div>
);

const formatStepTime = (ts: string): string =>
  new Date(ts).toLocaleString([], {
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
